import React, { useMemo, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Building2, User, Calendar, FileText } from 'lucide-react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions, FEATURES } from '../../../shared/hooks/usePermissions';
import { listarClientes } from '../../clientes/api';
import { listarUsuarios } from '../../usuarios/api';
import {
  useAnexosDaBase,
  useAprovacoesDaBase,
  useBaseComercial,
  useDominio,
  useHistoricoDaBase,
  useItensDaBase,
  useServicosDaBase,
  useVersoesDaBase,
} from '../hooks';
import type {
  BaseComercial,
  BaseComercialItem,
  BaseComercialServico,
  SituacaoBaseComercial,
} from '../api';
import { formatCurrency, formatVigencia, SITUACAO_LABEL } from '../utils/format';
import { downloadAuthenticatedFile } from '../../../shared/lib/files';
import {
  CodigoVersaoBadge,
  ReajustePendenteBadge,
  SituacaoBadge,
  SomenteLeituraBadge,
} from '../components/BaseComercialBadges';
import {
  AprovarBaseModal,
  CancelarBaseModal,
  PublicarBaseModal,
  RejeitarBaseModal,
  SolicitarAprovacaoModal,
  SubstituirBaseModal,
  SuspenderBaseModal,
} from '../components/BaseComercialActionModals';
import { BaseComercialHistorico } from '../components/BaseComercialHistorico';

/**
 * Detalhe da Base Comercial (tasks 11.1, 11.4).
 *
 * Header com badges + ações de ciclo de vida (solicitar aprovação,
 * aprovar, rejeitar, publicar, suspender, cancelar, substituir) — cada
 * ação exposta apenas conforme a permissão do perfil. Seções read-only
 * de dados, estrutura, anexos, aprovações, versões e histórico.
 */
export default function BaseComercialDetalhePage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = usePermissions();
  const { data: base, isLoading, isError, error } = useBaseComercial(id);
  const { data: servicos } = useServicosDaBase(id);
  const { data: itens } = useItensDaBase(id);

  const [openModal, setOpenModal] = useState<
    | 'solicitar'
    | 'aprovar'
    | 'rejeitar'
    | 'publicar'
    | 'suspender'
    | 'cancelar'
    | 'substituir'
    | null
  >(null);

  // Lookups por ID → label.
  const { data: clientesResp } = useQuery({
    queryKey: ['picker', 'clientes'],
    queryFn: () => listarClientes({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const { data: usuariosResp } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });

  const { data: servicosDominio } = useDominio('servicos');
  const { data: perfisDominio } = useDominio('perfis');
  const { data: precificacaoDominio } = useDominio('precificacao');
  const { data: indicesDominio } = useDominio('indices');

  const servicoLabel = (slug: string) =>
    servicosDominio?.find((s) => s.slug === slug)?.nome ?? slug;
  const perfilLabel = (slug: string) =>
    perfisDominio?.find((p) => p.slug === slug)?.nome ?? slug;
  const precificacaoLabel = (slug: string) =>
    precificacaoDominio?.find((p) => p.slug === slug)?.nome ?? slug;
  const indiceLabel = (slug: string) =>
    indicesDominio?.find((i) => i.slug === slug)?.nome ?? slug;

  const clienteInfo = useMemo(
    () => (clientesResp?.data ?? []).find((c) => c.id === base?.clientId) ?? null,
    [base, clientesResp],
  );
  const responsavelInfo = useMemo(
    () => (usuariosResp?.data ?? []).find((u) => u.id === base?.responsavelComercialId) ?? null,
    [base, usuariosResp],
  );

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-2/3 mb-4" />
        <Skeleton className="h-4 w-1/2 mb-8" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !base) {
    return (
      <div className="p-6 space-y-6">
        <button
          onClick={() => navigate('/base-comercial')}
          className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar para a lista
        </button>
        <Alert variant="error">
          {isError
            ? `Erro ao carregar Base Comercial: ${(error as Error)?.message ?? 'Falha desconhecida.'}`
            : 'Base Comercial não encontrada ou sem permissão de visualização.'}
        </Alert>
      </div>
    );
  }

  const editavel =
    base.situacao === 'em_elaboracao' || base.situacao === 'em_revisao';

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <button
        onClick={() => navigate('/base-comercial')}
        className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar para a lista
      </button>

      {/* Header */}
      <div className="mb-6 flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <CodigoVersaoBadge codigo={base.codigo} versao={base.versao} />
            <SituacaoBadge situacao={base.situacao} />
            {base.reajustePendente && <ReajustePendenteBadge />}
            {!editavel && <SomenteLeituraBadge />}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/base-comercial/${base.id}/versoes`)}
            >
              <FileText className="h-3.5 w-3.5" />
              Versões
            </Button>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">{base.nome}</h1>
        <p className="text-sm text-gray-600">{base.modalidade}</p>
      </div>

      {/* Ciclo de vida (task 11.1) */}
      <Section title="Ciclo de vida">
        <div className="flex flex-wrap gap-2">
          {base.situacao === 'em_elaboracao' && can(FEATURES.baseComercial, 'edit') && (
            <ActionButton variant="primary" onClick={() => setOpenModal('solicitar')}>
              Solicitar aprovação
            </ActionButton>
          )}
          {base.situacao === 'em_revisao' && can(FEATURES.baseComercial, 'edit') && (
            <ActionButton variant="primary" onClick={() => setOpenModal('solicitar')}>
              Solicitar aprovação
            </ActionButton>
          )}
          {base.situacao === 'em_aprovacao' && can(FEATURES.baseComercialAprovacao, 'edit') && (
            <>
              <ActionButton variant="success" onClick={() => setOpenModal('aprovar')}>
                Aprovar
              </ActionButton>
              <ActionButton variant="danger" onClick={() => setOpenModal('rejeitar')}>
                Rejeitar
              </ActionButton>
            </>
          )}
          {base.situacao === 'aprovada' && can(FEATURES.baseComercialPublicacao, 'edit') && (
            <ActionButton variant="primary" onClick={() => setOpenModal('publicar')}>
              Publicar
            </ActionButton>
          )}
          {base.situacao === 'publicada' && can(FEATURES.baseComercialPublicacao, 'edit') && (
            <ActionButton variant="warning" onClick={() => setOpenModal('suspender')}>
              Suspender
            </ActionButton>
          )}
          {base.situacao === 'vigente' && can(FEATURES.baseComercialPublicacao, 'edit') && (
            <>
              <ActionButton variant="warning" onClick={() => setOpenModal('suspender')}>
                Suspender
              </ActionButton>
              <ActionButton variant="primary" onClick={() => setOpenModal('substituir')}>
                Substituir
              </ActionButton>
            </>
          )}
          {base.situacao === 'suspensa' && can(FEATURES.baseComercialPublicacao, 'edit') && (
            <ActionButton variant="primary" onClick={() => setOpenModal('publicar')}>
              Reativar
            </ActionButton>
          )}
          {(editavel || base.situacao === 'publicada' || base.situacao === 'vigente') &&
            can(FEATURES.baseComercialCancelamento, 'edit') && (
              <ActionButton variant="danger" onClick={() => setOpenModal('cancelar')}>
                Cancelar
              </ActionButton>
            )}
        </div>
      </Section>

      {/* Indicadores */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <IndicatorCard label="Valor total" value={formatCurrency(base.valorTotal, base.moeda)} />
        <IndicatorCard
          label="Receita estimada"
          value={formatCurrency(base.receitaEstimada, base.moeda)}
        />
        <IndicatorCard
          label="Margem estimada"
          value={
            base.margemEstimadaPercentual
              ? `${Number(base.margemEstimadaPercentual).toFixed(2)}%`
              : '—'
          }
        />
        <IndicatorCard
          label="Vigência"
          value={formatVigencia(base.dataInicio, base.dataFim)}
        />
      </div>

      <Section title="Dados Gerais">
        <DataGrid>
          <Field icon={<Building2 />} label="Cliente">
            {clienteInfo ? (
              <Link to={`/clientes/${clienteInfo.id}`} className="text-blue-600 hover:underline">
                {clienteInfo.razaoSocial}
                <span className="ml-1 text-xs text-gray-500">({clienteInfo.code})</span>
              </Link>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Field>
          <Field icon={<User />} label="Responsável comercial">
            {responsavelInfo ? (
              <div>
                <div>{responsavelInfo.name}</div>
                <div className="text-xs text-gray-500">{responsavelInfo.email}</div>
              </div>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Field>
          <Field label="Modalidade">{base.modalidade}</Field>
          <Field label="Moeda">{base.moeda}</Field>
          <Field label="Escopo">{base.escopo ?? '—'}</Field>
          <Field label="Situação">{SITUACAO_LABEL[base.situacao]}</Field>
          {base.reajustePeriodicidade !== 'nenhuma' && (
            <Field label="Reajuste">
              {reajusteResumo(base, indiceLabel)}
            </Field>
          )}
          {base.descontoMaximoPercentual != null && (
            <Field label="Desconto máximo">
              {`${Number(base.descontoMaximoPercentual).toFixed(2)}%`}
            </Field>
          )}
          {base.condicoesFaturamento && (
            <Field label="Condições de faturamento">{base.condicoesFaturamento}</Field>
          )}
          {base.observacoesTributarias && (
            <Field label="Observações tributárias">{base.observacoesTributarias}</Field>
          )}
        </DataGrid>
      </Section>

      {/* Estrutura (serviços/perfis/itens) */}
      <Section title="Estrutura (serviços, perfis e precificação)">
        <EstruturaTable
          servicos={servicos ?? []}
          itens={itens ?? []}
          moeda={base.moeda}
          servicoLabel={servicoLabel}
          perfilLabel={perfilLabel}
          precificacaoLabel={precificacaoLabel}
          editavel={editavel && can(FEATURES.baseComercial, 'edit')}
        />
      </Section>

      <AnexosSection base={base} />

      <AprovacoesSection base={base} />

      <Section title="Histórico">
        <BaseComercialHistorico baseId={base.id} />
      </Section>

      <SolicitarAprovacaoModal
        base={base}
        isOpen={openModal === 'solicitar'}
        onClose={() => setOpenModal(null)}
        onSuccess={() => toast.success('Aprovação solicitada.')}
      />
      <AprovarBaseModal
        base={base}
        isOpen={openModal === 'aprovar'}
        onClose={() => setOpenModal(null)}
        onSuccess={() => toast.success('Base aprovada.')}
      />
      <RejeitarBaseModal
        base={base}
        isOpen={openModal === 'rejeitar'}
        onClose={() => setOpenModal(null)}
        onSuccess={() => toast.info('Base rejeitada. Devolvida para Em Revisão.')}
      />
      <PublicarBaseModal
        base={base}
        isOpen={openModal === 'publicar'}
        onClose={() => setOpenModal(null)}
        onSuccess={() => toast.success('Base publicada.')}
      />
      <SuspenderBaseModal
        base={base}
        isOpen={openModal === 'suspender'}
        onClose={() => setOpenModal(null)}
        onSuccess={() => toast.warning('Base suspensa.')}
      />
      <CancelarBaseModal
        base={base}
        isOpen={openModal === 'cancelar'}
        onClose={() => setOpenModal(null)}
        onSuccess={() => toast.warning('Base cancelada.')}
      />
      <SubstituirBaseModal
        base={base}
        isOpen={openModal === 'substituir'}
        onClose={() => setOpenModal(null)}
        onSuccess={() => toast.success('Base substituída.')}
      />
    </div>
  );
}

function reajusteResumo(
  b: BaseComercial,
  indiceLabel: (slug: string) => string,
): React.ReactNode {
  const label =
    b.reajustePeriodicidade === 'indice'
      ? indiceLabel(b.reajusteIndexSlug ?? '')
      : b.reajustePeriodicidade;
  if (b.reajusteIndexSlug === 'fixo' && b.reajustePercentualFixo != null) {
    return `${label} • ${Number(b.reajustePercentualFixo).toFixed(2)}% fixo`;
  }
  return label;
}

function EstruturaTable({
  servicos,
  itens,
  moeda,
  servicoLabel,
  perfilLabel,
  precificacaoLabel,
}: {
  servicos: BaseComercialServico[];
  itens: BaseComercialItem[];
  moeda: string;
  servicoLabel: (s: string) => string;
  perfilLabel: (s: string) => string;
  precificacaoLabel: (s: string) => string;
  editavel?: boolean;
}) {
  if (servicos.length === 0) {
    return <p className="text-sm text-gray-500">Nenhum serviço cadastrado.</p>;
  }
  return (
    <div className="space-y-4">
      {servicos.map((sv) => (
        <div key={sv.id} className="rounded-lg border border-gray-200 p-3">
          <div className="text-sm font-semibold text-gray-900">
            {servicoLabel(sv.serviceTypeSlug)}
            {sv.descricao && (
              <span className="ml-2 text-xs font-normal text-gray-500">{sv.descricao}</span>
            )}
          </div>
          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left font-medium text-gray-700">Perfil</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-700">Precificação</th>
                  <th className="px-2 py-2 text-right font-medium text-gray-700">Qtd</th>
                  <th className="px-2 py-2 text-right font-medium text-gray-700">Valor unit.</th>
                  <th className="px-2 py-2 text-right font-medium text-gray-700">Custo unit.</th>
                  <th className="px-2 py-2 text-right font-medium text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {itens
                  .filter((i) => i.commercialBaseServiceId === sv.id)
                  .map((i) => (
                    <tr key={i.id}>
                      <td className="px-2 py-2 text-gray-800">{perfilLabel(i.professionalProfileSlug)}</td>
                      <td className="px-2 py-2 text-gray-700">{precificacaoLabel(i.pricingModelSlug)}</td>
                      <td className="px-2 py-2 text-right text-gray-700">{i.quantidade}</td>
                      <td className="px-2 py-2 text-right text-gray-700">
                        {formatCurrency(i.valorUnitario, moeda as never)}
                      </td>
                      <td className="px-2 py-2 text-right text-gray-700">
                        {formatCurrency(i.custoUnitario, moeda as never)}
                      </td>
                      <td className="px-2 py-2 text-right font-medium text-gray-900">
                        {formatCurrency(i.valorTotalItem, moeda as never)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnexosSection({ base }: { base: BaseComercial }) {
  const { data: anexos, isLoading } = useAnexosDaBase(base.id);

  const handleDownload = useCallback(
    async (anexoId: string, nomeArquivo: string) => {
      await downloadAuthenticatedFile(
        `/api/commercial-bases/${base.id}/attachments/${anexoId}/download`,
        nomeArquivo,
      );
    },
    [base.id],
  );

  return (
    <Section title="Anexos">
      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : anexos && anexos.length > 0 ? (
        <ul className="divide-y divide-gray-100">
          {anexos.map((a) => (
            <li key={a.id} className="flex items-center justify-between py-2 text-sm">
              <div>
                <button
                  type="button"
                  onClick={() => handleDownload(a.id, a.nomeArquivo)}
                  className="text-blue-600 hover:underline"
                >
                  {a.nomeArquivo}
                </button>
                <span className="ml-2 text-xs text-gray-500">
                  v{a.versaoOrigem} • {a.categoria}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">Nenhum anexo vinculado.</p>
      )}
      <p className="mt-2 text-xs text-gray-500">
        Anexar é permitido em versão publicada (não altera condições aprovadas). O anexo fica
        visível a partir da versão em que foi vinculado (RN-COM-028).
      </p>
    </Section>
  );
}

function AprovacoesSection({ base }: { base: BaseComercial }) {
  const { data: aprovacoes, isLoading } = useAprovacoesDaBase(base.id);
  return (
    <Section title="Aprovações">
      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : aprovacoes && aprovacoes.length > 0 ? (
        <ul className="divide-y divide-gray-100">
          {aprovacoes.map((a) => (
            <li key={a.id} className="py-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-800">
                  {a.decisao === 'pendente' ? 'Pendente' : a.decisao === 'aprovada' ? 'Aprovada' : 'Rejeitada'}
                </span>
                <span className="text-xs text-gray-500">{formatDateTime(a.solicitadoEm)}</span>
              </div>
              {a.justificativa && (
                <p className="mt-1 text-xs text-gray-600">Justificativa: {a.justificativa}</p>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">Nenhuma aprovação registrada.</p>
      )}
    </Section>
  );
}

type ActionVariant = 'primary' | 'success' | 'danger' | 'warning';

const ACTION_STYLES: Record<ActionVariant, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700',
  success: 'bg-green-600 text-white hover:bg-green-700',
  danger: 'bg-red-600 text-white hover:bg-red-700',
  warning: 'bg-yellow-500 text-white hover:bg-yellow-600',
};

const ActionButton: React.FC<
  React.PropsWithChildren<{ variant?: ActionVariant; onClick: () => void }>
> = ({ children, variant = 'primary', onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${ACTION_STYLES[variant]}`}
  >
    {children}
  </button>
);

const Section: React.FC<React.PropsWithChildren<{ title: string }>> = ({ title, children }) => (
  <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">{title}</h2>
    {children}
  </section>
);

const DataGrid: React.FC<React.PropsWithChildren> = ({ children }) => (
  <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">{children}</dl>
);

const Field: React.FC<{ label: string; icon?: React.ReactNode; children: React.ReactNode }> = ({
  label,
  icon,
  children,
}) => (
  <div>
    <dt className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
      {icon && <span className="text-gray-400">{icon}</span>}
      {label}
    </dt>
    <dd className="mt-0.5 text-sm text-gray-800">{children}</dd>
  </div>
);

const IndicatorCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{value}</div>
  </div>
);

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR');
}
